import React from 'react'
import moment from 'moment'

import useTranslation from '@/Hooks/UseTranslation'
import { WeeklyCommitment, WeeklyWorkPlan } from '@/Services/WeeklyWorkPlanService'

import './WeeklyPlanStatusBar.scss'

export type WeeklyPlanLoadStatus = 'idle' | 'loading' | 'loaded' | 'error'

interface Props {
  loadStatus: WeeklyPlanLoadStatus
  plan: WeeklyWorkPlan | null
  commitments: WeeklyCommitment[]
  onRetry: () => void
  actionLabel?: string
  onAction?: () => void
}

const formatPercent = (value?: number | null) => value == null
  ? '—'
  : `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`

const WeeklyPlanStatusBar = ({ loadStatus, plan, commitments, onRetry, actionLabel, onAction }: Props) => {
  const { t } = useTranslation()

  const eligible = commitments.filter((row) => row.eligible)
  const ready = eligible.filter((row) => row.restrictionSnapshot?.state !== 'blocked').length
  const blocked = eligible.length - ready
  const resolved = eligible.filter((row) => row.status === 'resolved').length
  const declared = eligible.filter((row) => row.status === 'declared_complete').length
  const pending = Math.max(eligible.length - resolved, 0)

  const status = plan?.status || 'none'
  const statusLabel = loadStatus === 'error'
    ? t('WEEKLY_PLAN:Status_error')
    : loadStatus === 'loading' || loadStatus === 'idle'
      ? t('WEEKLY_PLAN:Status_loading')
      : t(`WEEKLY_PLAN:Status_${status}`)
  const updatedAt = plan?.updatedAt ? moment(plan.updatedAt).format('DD/MM/YYYY HH:mm') : ''

  const summary = () => {
    if (loadStatus === 'loading' || loadStatus === 'idle') return t('WEEKLY_PLAN:Loading')
    if (loadStatus === 'error') return t('WEEKLY_PLAN:LoadError')
    if (!plan) return t('WEEKLY_PLAN:NoPlanDescription')
    if (plan.status === 'draft') return t('WEEKLY_PLAN:DraftSummary')
      .replace('{total}', `${eligible.length}`)
      .replace('{ready}', `${ready}`)
      .replace('{blocked}', `${blocked}`)
    if (plan.status === 'published') return t('WEEKLY_PLAN:PublishedSummary')
      .replace('{total}', `${eligible.length}`)
      .replace('{declared}', `${declared}`)
      .replace('{pending}', `${pending}`)

    return t('WEEKLY_PLAN:ClosedSummary')
      .replace('{numerator}', `${plan.ppcResult?.numerator || 0}`)
      .replace('{denominator}', `${plan.ppcResult?.denominator || plan.denominator || 0}`)
  }

  return (
    <section className={`weekly-plan-status weekly-plan-status--${status}`} aria-label={t('WEEKLY_PLAN:AriaLabel')}>
      <div className="weekly-plan-status__identity">
        <div className="weekly-plan-status__state">
          <span className="weekly-plan-status__dot" aria-hidden="true" />
          <strong>{statusLabel}</strong>
          {plan && <span className="weekly-plan-status__version">{t('WEEKLY_PLAN:Version')} {plan.version}</span>}
          {plan?.retrospective && <span className="weekly-plan-status__retrospective">{t('WEEKLY_PLAN:RetrospectiveBadge')}</span>}
        </div>
        {updatedAt && <small>{t('WEEKLY_PLAN:LastUpdate')} {updatedAt}</small>}
      </div>

      <div className="weekly-plan-status__summary">
        <span>{summary()}</span>
        {plan?.status === 'closed' && (
          <strong className="weekly-plan-status__ppc">PPC {formatPercent(plan.ppcResult?.value)}</strong>
        )}
      </div>

      {loadStatus === 'error' && (
        <button type="button" className="btn weekly-plan-status__retry" onClick={onRetry}>
          <i className="fas fa-sync-alt" aria-hidden="true" />
          {t('WEEKLY_PLAN:Retry')}
        </button>
      )}
      {loadStatus === 'loaded' && actionLabel && onAction && (
        <button type="button" className="btn weekly-plan-status__action" onClick={onAction}>
          <i className="fas fa-clipboard-check" aria-hidden="true" />
          {actionLabel}
        </button>
      )}
    </section>
  )
}

export default WeeklyPlanStatusBar
